import React, { useMemo, useState } from 'react';
import { DeviceSchedule, DeviceScheduleInput, DeviceScheduleRunPlan, ScheduleAction, SwitchableDevice } from '../../services/deviceSchedulesApi';
import { ContactGroup } from '../../services/alertsApi';
import { ui } from '../../styles/shared';

/** Redigerare för ett schema: enheter, tillfällen, vem som larmas, besked och effektkontroll. */

interface Props {
    schedule: DeviceSchedule | null;
    devices: SwitchableDevice[];
    groups: ContactGroup[];
    /** Andra schemans planerade dagar (dag → schemanamn), så krockar syns i kalendern. */
    others?: Map<string, string[]>;
    onSave: (body: DeviceScheduleInput) => Promise<void>;
    onCancel: () => void;
}

const input = ui.input;
const label = ui.label;
const hint: React.CSSProperties = { fontSize: 12, color: '#777', marginTop: 4 };
const section: React.CSSProperties = { borderTop: '1px solid #eee', paddingTop: 14, marginTop: 14 };

/** ISO-tid → värde för <input type="datetime-local"> i webbläsarens tid. */
const toLocalInput = (iso: string): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v: string): string => {
    const d = new Date(v);
    return isNaN(d.getTime()) ? '' : d.toISOString();
};

/**
 * Ett tillfälle medan det redigeras. Tiden hålls som fältets egen text (lokal tid), så att
 * ett halvifyllt fält inte blir tomt eller flyttar raden. uid håller raden stabil i listan.
 */
interface EditRun { uid: number; id?: number; local: string; action: ScheduleAction }
let nextUid = 1;
const toEditRun = (r: DeviceScheduleRunPlan): EditRun => ({ uid: nextUid++, id: r.id, local: toLocalInput(r.run_at), action: r.action });

/** Förslag på nästa tillfälle: dagen efter det senaste i listan (eller i morgon), kl 06:00 för PÅ och 22:00 för AV. */
const suggestNext = (runs: EditRun[], action: ScheduleAction): string => {
    const last = runs.map(r => new Date(r.local)).filter(d => !isNaN(d.getTime())).sort((a, b) => b.getTime() - a.getTime())[0];
    const d = last ? new Date(last) : new Date();
    if (!last || action === 'ON') d.setDate(d.getDate() + 1);
    d.setHours(action === 'ON' ? 6 : 22, 0, 0, 0);
    return toLocalInput(d.toISOString());
};

const key = (deviceId: number, ep: number) => `${deviceId}:${ep}`;

// ── Kalendern: välj på-/av-tid en gång och bocka i dagarna ──────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');
export const dayKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const MONTH_NAMES = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const WEEKDAYS = ['mån', 'tis', 'ons', 'tor', 'fre', 'lör', 'sön'];

// Kalenderns färger: på-dag, av-dag, dagarna emellan (enheterna står på) och
// orange för dagar där ett annat schema också kör (hel ruta; kantlinje när
// dagen samtidigt är färgad av det här schemat).
const COLOR_ON = '#28a745';
const COLOR_OFF = '#dc3545';
const COLOR_BETWEEN = '#d4edda';
const COLOR_OTHER = '#fdba74';
const COLOR_OTHER_BORDER = '#f97316';
const legendDot: React.CSSProperties = { display: 'inline-block', width: 10, height: 10, borderRadius: 3, marginRight: 4, verticalAlign: 'middle' };

/**
 * Alltid 6 veckor om 7 dagar, med förra och nästa månads dagar i kanterna så att
 * ett drag kan gå över ett månadsskifte. Veckan börjar på måndag.
 */
const monthGrid = (year: number, month: number): Date[][] => {
    const first = new Date(year, month, 1);
    const lead = (first.getDay() + 6) % 7; // 0 = måndag
    const d = new Date(year, month, 1 - lead);
    const rows: Date[][] = [];
    for (let w = 0; w < 6; w++) {
        const row: Date[] = [];
        for (let i = 0; i < 7; i++) { row.push(new Date(d)); d.setDate(d.getDate() + 1); }
        rows.push(row);
    }
    return rows;
};

const DeviceScheduleEditor: React.FC<Props> = ({ schedule, devices, groups, others, onSave, onCancel }) => {
    const [form, setForm] = useState({
        name: schedule?.name ?? '',
        fail_group_id: schedule?.fail_group_id ?? 0,
        notify_ok: !!schedule?.ok_group_id,
        ok_group_id: schedule?.ok_group_id ?? 0,
        power_check: schedule?.power_check ?? false,
        power_property: schedule?.power_property ?? 'power',
        power_min_watts: schedule?.power_min_watts != null ? String(schedule.power_min_watts) : '',
        power_delay_seconds: String(schedule?.power_delay_seconds ?? 60),
        active: schedule?.active ?? true,
    });
    const [chosen, setChosen] = useState<Set<string>>(() => new Set((schedule?.devices ?? []).map(d => key(d.device_id, d.io_endpoint))));
    // Ordningen ändras inte medan man skriver (då skulle markören hoppa till en annan rad);
    // listan från servern kommer redan i tidsordning och nya tillfällen hamnar sist.
    const [runs, setRuns] = useState<EditRun[]>(() => (schedule?.runs ?? []).map(toEditRun));
    const [filter, setFilter] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const set = (patch: Partial<typeof form>) => setForm(prev => ({ ...prev, ...patch }));

    const toggle = (k: string, on: boolean) => setChosen(prev => {
        const next = new Set(prev);
        if (on) next.add(k); else next.delete(k);
        return next;
    });

    const shown = useMemo(() => {
        const f = filter.trim().toLowerCase();
        if (!f) return devices;
        return devices.filter(d => d.name.toLowerCase().includes(f) || d.zones.some(z => z.toLowerCase().includes(f)));
    }, [devices, filter]);

    const chosenDevices = devices.filter(d => d.type === 'io_module'
        ? d.io_endpoints.some(ep => chosen.has(key(d.id, ep)))
        : chosen.has(key(d.id, 0)));
    const withoutPower = chosenDevices.filter(d => !d.has_power);
    const failGroup = groups.find(g => g.id === Number(form.fail_group_id));

    const setRun = (uid: number, patch: Partial<EditRun>) =>
        setRuns(prev => prev.map(r => (r.uid === uid ? { ...r, ...patch } : r)));
    const addRun = (action: ScheduleAction) => setRuns(prev => [...prev, { uid: nextUid++, local: suggestNext(prev, action), action }]);
    const removeRun = (uid: number) => setRuns(prev => prev.filter(r => r.uid !== uid));

    // Kalendern: på-/av-tid skrivs en gång, sedan bockas dagarna i. Varje ibockad dag
    // blir ett PÅ- och ett AV-tillfälle; en ibockad dag som klickas igen töms på tillfällen.
    const [onTime, setOnTime] = useState('06:00');
    const [offTime, setOffTime] = useState('22:00');
    const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
    const [showList, setShowList] = useState(false);
    const todayKey = dayKey(new Date());
    const stepMonth = (delta: number) => setCalMonth(({ year, month }) => {
        const d = new Date(year, month + delta, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });

    // Dagfärgerna: på-dag, av-dag och dagarna mellan ett på och nästa av (enheterna står på).
    const dayInfo = useMemo(() => {
        const evts = runs
            .filter(r => r.local.length >= 10)
            .slice()
            .sort((a, b) => (a.local < b.local ? -1 : 1));
        const on = new Set<string>(), off = new Set<string>(), between = new Set<string>();
        let pendingOn: string | null = null;
        for (const e of evts) {
            const day = e.local.slice(0, 10);
            if (e.action === 'ON') { on.add(day); if (!pendingOn) pendingOn = day; }
            else {
                off.add(day);
                if (pendingOn && pendingOn < day) {
                    const d = new Date(pendingOn); d.setDate(d.getDate() + 1);
                    for (; dayKey(d) < day; d.setDate(d.getDate() + 1)) between.add(dayKey(d));
                }
                pendingOn = null;
            }
        }
        return { on, off, between };
    }, [runs]);

    /**
     * Tar bort dagens tillfällen, tillsammans med motparten om paret sträcker sig
     * över flera dagar (så inget ensamt på eller av blir kvar).
     */
    const removePairAt = (k: string) => {
        setRuns(prev => {
            const sorted = prev.filter(r => r.local.length >= 10).slice().sort((a, b) => (a.local < b.local ? -1 : 1));
            const doomed = new Set<number>(sorted.filter(r => r.local.slice(0, 10) === k).map(r => r.uid));
            for (let i = 0; i < sorted.length; i++) {
                if (!doomed.has(sorted[i].uid)) continue;
                if (sorted[i].action === 'ON') {
                    const next = sorted.slice(i + 1).find(r => r.action === 'OFF');
                    if (next) doomed.add(next.uid);
                } else {
                    const prevOn = sorted.slice(0, i).reverse().find(r => r.action === 'ON');
                    if (prevOn) doomed.add(prevOn.uid);
                }
            }
            return prev.filter(r => !doomed.has(r.uid));
        });
    };

    /** Lägger till ett på-tillfälle dag a och ett av-tillfälle dag b (samma dag när a = b). */
    const addRange = (a: string, b: string) => {
        if (!onTime || !offTime) return;
        let offDayKey = b;
        if (a === b && offTime <= onTime) {
            // Av-tid före eller lika med på-tid betyder över midnatt: AV hamnar dagen efter.
            const d = new Date(b); d.setDate(d.getDate() + 1);
            offDayKey = dayKey(d);
        }
        setRuns(prev => [...prev,
            { uid: nextUid++, local: `${a}T${onTime}`, action: 'ON' as ScheduleAction },
            { uid: nextUid++, local: `${offDayKey}T${offTime}`, action: 'OFF' as ScheduleAction },
        ]);
    };

    /**
     * Dra i kalendern: håll ner vänster musknapp på dagen enheterna ska slås på och
     * släpp på dagen de ska slås av (samma dag = på och av samma dag). Ett tryck på en
     * på-/av-dag tar i stället bort paret. Mellandagar rörs inte — de ändras via listan.
     */
    const [drag, setDrag] = useState<{ start: string; end: string; removing: boolean } | null>(null);
    const dragRef = React.useRef(drag);
    dragRef.current = drag;

    const dragDown = (k: string) => {
        if (dayInfo.between.has(k) && !dayInfo.on.has(k) && !dayInfo.off.has(k)) return;
        setDrag({ start: k, end: k, removing: dayInfo.on.has(k) || dayInfo.off.has(k) });
    };
    const dragEnter = (k: string) => setDrag(prev => (prev && !prev.removing ? { ...prev, end: k } : prev));
    React.useEffect(() => {
        if (!drag) return;
        const up = () => {
            const d = dragRef.current;
            setDrag(null);
            if (!d) return;
            if (d.removing) { if (d.start === d.end) removePairAt(d.start); return; }
            const [a, b] = [d.start, d.end].sort();
            addRange(a, b);
        };
        window.addEventListener('mouseup', up);
        return () => window.removeEventListener('mouseup', up);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [!!drag]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!form.name.trim()) { setError('Schemat måste ha ett namn'); return; }
        if (chosen.size === 0) { setError('Välj minst en enhet'); return; }
        if (!form.fail_group_id) { setError('Välj vilken grupp som larmas om någon enhet inte startar'); return; }
        if (form.notify_ok && !form.ok_group_id) { setError('Välj vilken grupp som får beskedet när allt gått bra'); return; }
        if (form.power_check && (form.power_min_watts === '' || Number(form.power_min_watts) < 0)) { setError('Ange hur många watt enheterna minst ska dra'); return; }
        if (runs.some(r => !fromLocalInput(r.local))) { setError('Fyll i datum och tid för alla tillfällen'); setShowList(true); return; }
        setSaving(true);
        try {
            await onSave({
                name: form.name.trim(),
                fail_group_id: Number(form.fail_group_id),
                ok_group_id: form.notify_ok ? Number(form.ok_group_id) : null,
                power_check: form.power_check,
                power_property: form.power_property.trim() || 'power',
                power_min_watts: form.power_min_watts === '' ? null : Number(form.power_min_watts),
                power_delay_seconds: Number(form.power_delay_seconds) || 0,
                active: form.active,
                devices: Array.from(chosen).map(k => { const [d, ep] = k.split(':').map(Number); return { device_id: d, io_endpoint: ep }; }),
                runs: runs.map(r => ({ id: r.id, run_at: fromLocalInput(r.local), action: r.action })),
            });
        } catch (err: any) {
            setError(err.message || 'Kunde inte spara schemat');
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} style={{ background: 'white', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <h2 style={{ marginTop: 0 }}>{schedule ? 'Ändra schema' : 'Nytt schema'}</h2>
            {error && <div style={{ background: '#f8d7da', color: '#721c24', padding: 10, borderRadius: 4, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                <div>
                    <label style={label}>Namn</label>
                    <input value={form.name} onChange={e => set({ name: e.target.value })} placeholder="T.ex. Kylar kök, lågsäsong" style={input} />
                </div>
                <div>
                    <label style={label}>Larma om någon enhet inte startar <a href="/contacts" style={{ fontWeight: 'normal', fontSize: 12, marginLeft: 6 }}>Hantera grupper</a></label>
                    <select value={form.fail_group_id} onChange={e => set({ fail_group_id: Number(e.target.value) })} style={input}>
                        <option value={0}>Välj kontaktgrupp</option>
                        {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.members.length} pers.)</option>)}
                    </select>
                    {failGroup && failGroup.members.length === 0
                        ? <div style={{ ...hint, color: '#b02a37' }}>Gruppen har inga kontakter. Lägg till någon först, annars går larmet inte fram.</div>
                        : <div style={hint}>Krav. Larmet går som ett vanligt larm och följer kontakternas larmtider.</div>}
                </div>
            </div>

            {/* Enheter */}
            <div style={section}>
                <label style={label}>Enheter ({chosen.size} valda)</label>
                <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Sök på namn eller plats" style={{ ...input, marginBottom: 8 }} />
                <div style={{ border: '1px solid #ccc', borderRadius: 6, padding: 10, maxHeight: 260, overflowY: 'auto' }}>
                    {shown.length === 0 && <div style={{ fontSize: 13, color: '#888' }}>Inga enheter som går att slå på och av.</div>}
                    {shown.map(d => {
                        const extra = (
                            <span style={{ fontSize: 12, color: '#777' }}>
                                {d.zones.length > 0 && ` ${d.zones.join(', ')}`}
                                {d.has_power && ' · mäter effekt'}
                                {d.disabled && ' · avstängd i deviot, hoppas över'}
                            </span>
                        );
                        if (d.type === 'io_module') {
                            return (
                                <div key={d.id} style={{ padding: '4px 0', color: d.disabled ? '#999' : undefined }}>
                                    <div style={{ fontSize: 14 }}>{d.name}{extra}</div>
                                    {d.io_endpoints.length === 0 && <div style={{ fontSize: 12, color: '#888', paddingLeft: 18 }}>Inga utgångar kända än.</div>}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingLeft: 18 }}>
                                        {d.io_endpoints.map(ep => (
                                            <label key={ep} style={{ fontSize: 13, cursor: 'pointer' }}>
                                                <input type="checkbox" checked={chosen.has(key(d.id, ep))} onChange={e => toggle(key(d.id, ep), e.target.checked)} /> Utgång {ep}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, padding: '3px 0', cursor: 'pointer', color: d.disabled ? '#999' : undefined }}>
                                <input type="checkbox" checked={chosen.has(key(d.id, 0))} onChange={e => toggle(key(d.id, 0), e.target.checked)} />
                                <span>{d.name}{extra}</span>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Tillfällen: tider + kalender, med listan under för finjustering */}
            <div style={section}>
                <label style={label}>Tillfällen</label>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
                    <div>
                        <label style={{ ...label, fontSize: 12 }}>Slå på kl.</label>
                        <input type="time" value={onTime} onChange={e => setOnTime(e.target.value)} style={{ ...input, width: 'auto' }} />
                    </div>
                    <div>
                        <label style={{ ...label, fontSize: 12 }}>Slå av kl.</label>
                        <input type="time" value={offTime} onChange={e => setOffTime(e.target.value)} style={{ ...input, width: 'auto' }} />
                    </div>
                    <div style={{ ...hint, marginBottom: 8 }}>
                        Klicka på en dag för på och av samma dag, eller håll ner musknappen på på-dagen och släpp på av-dagen.
                        Ett klick på en markerad dag tar bort paret.
                        {offTime <= onTime && ' Av-tiden ligger före på-tiden, så avslaget hamnar dagen efter.'}
                    </div>
                </div>
                <div style={{ border: '1px solid #ccc', borderRadius: 6, padding: 10, maxWidth: 420, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <button type="button" onClick={() => stepMonth(-1)} style={ui.btnLightNeutral}>◀</button>
                        <strong style={{ fontSize: 14 }}>{MONTH_NAMES[calMonth.month]} {calMonth.year}</strong>
                        <button type="button" onClick={() => stepMonth(1)} style={ui.btnLightNeutral}>▶</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                        {WEEKDAYS.map(w => <div key={w} style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>{w}</div>)}
                        {monthGrid(calMonth.year, calMonth.month).flat().map(d => {
                            const outside = d.getMonth() !== calMonth.month;
                            const k = dayKey(d);
                            let isOn = dayInfo.on.has(k);
                            let isOff = dayInfo.off.has(k);
                            let isBetween = dayInfo.between.has(k) && !isOn && !isOff;
                            // Under ett pågående drag förhandsvisas perioden med samma färger.
                            if (drag && !drag.removing) {
                                const [a, b] = [drag.start, drag.end].sort();
                                if (k >= a && k <= b) {
                                    isOn = isOn || k === a;
                                    isOff = isOff || k === b;
                                    isBetween = !isOn && !isOff;
                                }
                            }
                            const otherNames = others?.get(k) ?? [];
                            const past = k < todayKey;
                            const hasOther = otherNames.length > 0 && !past;
                            const marked = isOn || isOff || isBetween;
                            const background = past ? '#f4f4f4'
                                : isOn && isOff ? `linear-gradient(135deg, ${COLOR_ON} 50%, ${COLOR_OFF} 50%)`
                                : isOn ? COLOR_ON
                                : isOff ? COLOR_OFF
                                : isBetween ? COLOR_BETWEEN
                                : hasOther ? COLOR_OTHER
                                : outside ? '#fbfbfb'
                                : 'white';
                            const title = [
                                isOn && isOff ? `Slås på ${onTime} och av samma dag` : isOn ? 'Slås på denna dag' : isOff ? 'Slås av denna dag' : isBetween ? 'Enheterna står på (ändras via listan)' : `Håll ner för på ${onTime}, släpp på av-dagen (${offTime})`,
                                otherNames.length ? `Andra scheman: ${otherNames.join(', ')}` : '',
                            ].filter(Boolean).join('\n');
                            return (
                                <button key={k} type="button" disabled={past} title={title}
                                    onMouseDown={e => { e.preventDefault(); dragDown(k); }}
                                    onMouseEnter={() => dragEnter(k)}
                                    style={{
                                        position: 'relative', padding: '7px 0', borderRadius: 5, fontSize: 13, fontFamily: 'inherit',
                                        // Orange kantlinje: dagen är färgad av det här schemat OCH ett annat schema kör samma dag.
                                        border: hasOther && marked ? `2px solid ${COLOR_OTHER_BORDER}`
                                            : `1px solid ${isOn || isOff ? 'transparent' : isBetween ? COLOR_BETWEEN : hasOther ? COLOR_OTHER_BORDER : '#ddd'}`,
                                        background,
                                        color: isOn || isOff ? 'white' : past ? '#bbb' : outside && !hasOther ? '#999' : '#333',
                                        cursor: past ? 'default' : 'pointer',
                                        fontWeight: k === todayKey ? 700 : 400,
                                        userSelect: 'none',
                                    }}>
                                    {d.getDate()}
                                </button>
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: '#555' }}>
                        <span><span style={{ ...legendDot, background: COLOR_ON }} /> slås på</span>
                        <span><span style={{ ...legendDot, background: COLOR_OFF }} /> slås av</span>
                        <span><span style={{ ...legendDot, background: COLOR_BETWEEN }} /> står på</span>
                        <span><span style={{ ...legendDot, background: COLOR_OTHER, border: `1px solid ${COLOR_OTHER_BORDER}` }} /> annat schema samma dag</span>
                    </div>
                </div>
                {runs.length === 0 && <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>Inga planerade tillfällen. Bocka i dagar i kalendern, eller lägg till enstaka i listan.</div>}
                <button type="button" onClick={() => setShowList(v => !v)} style={ui.btnLight}>
                    {showList ? 'Dölj listan' : `Visa som lista och finjustera (${runs.length})`}
                </button>
                {showList && (
                    <div style={{ marginTop: 10 }}>
                        {runs.map(r => (
                            <div key={r.uid} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                                <input type="datetime-local" value={r.local} onChange={e => setRun(r.uid, { local: e.target.value })}
                                    style={{ ...input, width: 'auto', minWidth: 200 }} />
                                <select value={r.action} onChange={e => setRun(r.uid, { action: e.target.value as ScheduleAction })} style={{ ...input, width: 'auto' }}>
                                    <option value="ON">Slå på</option>
                                    <option value="OFF">Slå av</option>
                                </select>
                                <button type="button" onClick={() => removeRun(r.uid)} style={ui.btnLightDanger}>Ta bort</button>
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <button type="button" onClick={() => addRun('ON')} style={ui.btnLight}>+ Slå på</button>
                            <button type="button" onClick={() => addRun('OFF')} style={ui.btnLight}>+ Slå av</button>
                        </div>
                    </div>
                )}
                <div style={hint}>Varje tillfälle körs en gång. Hinner servern inte köra det inom 30 minuter räknas det som missat och larmar.</div>
            </div>

            {/* Besked och effekt */}
            <div style={{ ...section, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" checked={form.notify_ok} onChange={e => set({ notify_ok: e.target.checked })} />
                        Skicka besked när alla enheter är i rätt läge
                    </label>
                    {form.notify_ok && (
                        <select value={form.ok_group_id} onChange={e => set({ ok_group_id: Number(e.target.value) })} style={{ ...input, marginTop: 6 }}>
                            <option value={0}>Välj kontaktgrupp</option>
                            {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.members.length} pers.)</option>)}
                        </select>
                    )}
                    <div style={hint}>Mejl och push till gruppen, ett per tillfälle.</div>
                </div>
                <div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" checked={form.power_check} onChange={e => set({ power_check: e.target.checked })} />
                        Kontrollera att enheterna drar ström efter påslag
                    </label>
                    {form.power_check && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                            <div>
                                <label style={{ ...label, fontSize: 12 }}>Minst (W)</label>
                                <input type="number" min={0} step="any" value={form.power_min_watts} onChange={e => set({ power_min_watts: e.target.value })} style={input} />
                            </div>
                            <div>
                                <label style={{ ...label, fontSize: 12 }}>Efter (sekunder)</label>
                                <input type="number" min={0} max={600} value={form.power_delay_seconds} onChange={e => set({ power_delay_seconds: e.target.value })} style={input} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ ...label, fontSize: 12 }}>Mätvärde</label>
                                <input value={form.power_property} onChange={e => set({ power_property: e.target.value })} style={input} />
                            </div>
                        </div>
                    )}
                    {form.power_check && withoutPower.length > 0 && (
                        <div style={{ ...hint, color: '#b02a37' }}>
                            Mäter inte effekt: {withoutPower.map(d => d.name).join(', ')}. De räknas som misslyckade vid påslag.
                        </div>
                    )}
                    {!form.power_check && <div style={hint}>Till exempel: efter 60 sekunder ska varje kyl dra mer än 20 W.</div>}
                </div>
            </div>

            <div style={{ ...section, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.active} onChange={e => set({ active: e.target.checked })} />
                    Schemat är på
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={onCancel} style={ui.btnSecondary}>Avbryt</button>
                    <button type="submit" disabled={saving} style={ui.btnPrimary}>{saving ? 'Sparar…' : 'Spara'}</button>
                </div>
            </div>
        </form>
    );
};

export default DeviceScheduleEditor;
